import { yupResolver } from "@hookform/resolvers/yup";
import { t } from "i18next";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import Button from "~/components/buttons/Button/Button.tsx";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint";
import { FormSectionGroup } from "~/components/formElements/FormSection";
import SubmitButton from "~/components/formElements/SubmitButton";
import AddressFormSection from "~/components/formSections/AddressFormSection";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import {
	DEFAULT_COUNTRY_ID,
	DEFAULT_LANGUAGE_ID,
	DEFAULT_SALES_FUNNEL_ID,
	DEFAULT_SALES_TYPE_ID,
} from "~/constants/form.ts";
import { useAuth } from "~/contexts/AuthContext";
import useCountrySelectOptions from "~/hooks/form/formOptionsData/useCountrySelectOptions.ts";
import useGenderSelectOptions from "~/hooks/form/formOptionsData/useGenderSelectOptions.ts";
import useLanguageSelectOptions from "~/hooks/form/formOptionsData/useLanguageSelectOptions.ts";
import useSalesFunnelSelectOptions from "~/hooks/form/formOptionsData/useSalesFunnelSelectOptions.ts";
import useSalesTypeSelectOptions from "~/hooks/form/formOptionsData/useSalesTypeSelectOptions.ts";
import useUserSelectOptions, { employeesFilterFn } from "~/hooks/form/formOptionsData/useUsersSelectOptions.ts";
import { useFormIsSubmittable } from "~/hooks/form/useFormIsSubmittable.ts";
import { useAllClients } from "~/modules/client/api/client/clientQueries.ts";
import {
	createClientContactPerson,
} from "~/modules/client/api/clientContactPerson/clientContactPersonApiDispatchers.ts";
import type {
	CreateClientContactPersonData,
} from "~/modules/client/api/clientContactPerson/clientContactPersonTypes.ts";
import ContactCompanyFormSection
	from "~/modules/client/components/contact/forms/formSections/ContactCompanyFormSection";
import ContactContactFormSection
	from "~/modules/client/components/contact/forms/formSections/ContactContactFormSection";
import ContactPersonalInformationFormSection
	from "~/modules/client/components/contact/forms/formSections/ContactPersonalInformationFormSection";
import ContactSalesFormSection from "~/modules/client/components/contact/forms/formSections/ContactSalesFormSection";
import { normalizeTKey } from "~/types/typeHelpers.ts";
import { formatDateToYYYYMMDD } from "~/utils/dateAndTimeUtils.ts";
import {
	mapDataToInputOptionsData,
	preventSubmitOnEnter,
	transformEmptyDateValueToNull,
} from "~/utils/form/formUtils.ts";

type CreateClientContactPersonFormProps = {
	onSuccess: (clientId: string) => void;
	onCancel: () => void;
	clientId?: string;
	companyId: string;
	disableClientSelect?: boolean;
};

interface CreateClientContactPersonFormData extends Omit<CreateClientContactPersonData, "dateOfBirth"> {
	dateOfBirth: Date | null;
}

const userFilterOptions = { filterFunction: employeesFilterFn };

const CreateClientContactPersonForm: React.FC<CreateClientContactPersonFormProps> = ({
	onSuccess,
	onCancel,
	companyId,
	clientId,
}) => {
	//const { t } = useTranslation();
	const [busy, setBusy] = useState(false);
	const { user } = useAuth();

	const schema = useMemo(() => {
		return yup.object({
			city: yup.string().default(null),
			clientId: yup.string().required(),
			countryId: yup.string().required(),
			dateOfBirth: yup.date().transform(transformEmptyDateValueToNull).nullable().default(null),
			emailBusiness: yup.string().email().default(null),
			emailPrivate: yup.string().email().default(null),
			firstName: yup.string().required(),
			genderId: yup.string().required(),
			houseNumber: yup.string().default(null),
			jobTitle: yup.string().default(null),
			languageId: yup.string().required(),
			lastName: yup.string().required(),
			managedBy: yup.string().required(),
			mobilePhone: yup.string().default(null),
			nobilityTitle: yup.string().default(null),
			phone: yup.string().default(null),
			salesFunnelId: yup.string().required(),
			salesTypeId: yup.string().required(),
			street: yup.string().default(null),
			title: yup.string().default(null),
			useInformalSalutation: yup.boolean().required(),
			zipCode: yup.string().default(null),
		});
	}, []);

	const defaultValues = useMemo(() => {
		return {
			city: "",
			clientId: clientId || "",
			countryId: DEFAULT_COUNTRY_ID,
			dateOfBirth: null,
			emailBusiness: "",
			emailPrivate: "",
			firstName: "",
			genderId: "",
			houseNumber: "",
			jobTitle: "",
			languageId: DEFAULT_LANGUAGE_ID,
			lastName: "",
			managedBy: user?.id || "",
			mobilePhone: "",
			nobilityTitle: "",
			phone: "",
			salesFunnelId: DEFAULT_SALES_FUNNEL_ID,
			salesTypeId: DEFAULT_SALES_TYPE_ID,
			street: "",
			title: "",
			useInformalSalutation: false,
			zipCode: "",
		};
	}, [user, clientId]);

	const {
		handleSubmit,
		control,
		formState: { errors, isDirty, isValid, isSubmitted },
		setValue,
		watch,
	} = useForm<CreateClientContactPersonFormData>({
		defaultValues: defaultValues,
		resolver: yupResolver<CreateClientContactPersonFormData>(schema),
	});
	const formIsSubmittable = useFormIsSubmittable({ isDirty, isSubmitted, isValid, isLoading: busy });
	const countrySelectOptions = useCountrySelectOptions();
	const genderSelectOptions = useGenderSelectOptions();
	const languageSelectOptions = useLanguageSelectOptions();
	const managerSelectOptions = useUserSelectOptions(userFilterOptions);
	const salesFunnelSelectOptions = useSalesFunnelSelectOptions();
	const salesTypeSelectOptions = useSalesTypeSelectOptions();

	const { data: allClientsData } = useAllClients();

	const clientSelectOptions = useMemo(() => {
		if (allClientsData) {
			const companiesClients = allClientsData.filter((client) => client.companyId === companyId);
			return mapDataToInputOptionsData(companiesClients, "id", "displayName");
		}
		return [];
	}, [allClientsData, companyId]);

	useEffect(() => {
		if (!clientId && clientSelectOptions.length === 1) {
			setValue("clientId", clientSelectOptions[0].value);
		}
	}, [clientSelectOptions, clientId, setValue]);

	const onSubmit: SubmitHandler<CreateClientContactPersonFormData> = async (data) => {
		setBusy(true);
		const dateOfBirth = data.dateOfBirth ? formatDateToYYYYMMDD(new Date(data.dateOfBirth)) : null;
		const newContactPersonData = { ...data, dateOfBirth };
		try {
			await createClientContactPerson(newContactPersonData.clientId, newContactPersonData);

			onSuccess(data.clientId);
		} catch (error) {
			console.log(error);
		}
	};

	const selectedClientId = watch("clientId");

	useEffect(() => {
		if (selectedClientId) {
			const selectedClientData = allClientsData?.find((client) => client.id === selectedClientId);
			if (selectedClientData) {
				setValue("street", selectedClientData.street);
				setValue("houseNumber", selectedClientData.houseNumber);
				setValue("zipCode", selectedClientData.zipCode);
				setValue("city", selectedClientData.city);
				setValue("countryId", selectedClientData.countryId);
			}
		}
	}, [selectedClientId, allClientsData, setValue]);

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			onKeyDown={preventSubmitOnEnter}
			className="flex min-h-full w-full flex-col justify-start"
		>
			<SidebarHeader>
				<SidebarHeaderHeadline>Neuen Kontakt anlegen</SidebarHeaderHeadline>
			</SidebarHeader>
			<SidebarContent>
				{busy && <SidebarBusyOverlay />}
				<FormSectionGroup>
					<ContactCompanyFormSection<CreateClientContactPersonFormData>
						control={control}
						disableClientSelect={!!clientId}
						clientSelectOptions={clientSelectOptions}
						errors={errors}
					/>

					<ContactPersonalInformationFormSection<CreateClientContactPersonFormData>
						control={control}
						errors={errors}
						genderSelectOptions={genderSelectOptions}
					/>

					<ContactContactFormSection<CreateClientContactPersonFormData>
						control={control} />

					<AddressFormSection
						control={control}
						countrySelectOptions={countrySelectOptions}
					/>

					<ContactSalesFormSection
						control={control}
						errors={errors}
						languageSelectOptions={languageSelectOptions}
						managerSelectOptions={managerSelectOptions}
						salesFunnelSelectOptions={salesFunnelSelectOptions}
						salesTypeSelectOptions={salesTypeSelectOptions}
					/>
				</FormSectionGroup>
			</SidebarContent>
			<SidebarFooter>
				<FormHasErrorsHint
					show={isSubmitted && !isValid}
					className="mr-2" />
				<SubmitButton busy={busy}
							  disabled={!formIsSubmittable}>
					{t(normalizeTKey("form:buttonTexts.save"))}
				</SubmitButton>
				<Button theme="none"
						onClick={onCancel}>
					{t(normalizeTKey("form:buttonTexts.cancel"))}
				</Button>
			</SidebarFooter>
		</form>
	);
};

export default CreateClientContactPersonForm;
